import { payrollPeriod, validateLaborRows, type LaborSnapshot, type PayrollSourceState, type SubmissionRevision } from './types';

const REVISION_COLUMNS = 'id,period_key,period_start,period_end,closing_day,version,status,rows,source_fingerprint,confirmed_by,confirmed_at,reopened_at,reopen_reason';
const object = (value: unknown): value is Record<string, any> => !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;
function singleResult(data: any): any { return Array.isArray(data) ? data.length === 1 ? data[0] : null : data; }
function canonical(value: any): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (object(value)) return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${canonical(value[key])}`).join(',')}}`;
  return JSON.stringify(value);
}
function requireResult(result: { data: any; error: any }, operation: string): any {
  if (result.error) throw new Error(`${operation}に失敗しました。${result.error.message ?? ''}`);
  const row = singleResult(result.data);
  if (!row) throw new Error(`${operation}の保存結果を確認できませんでした。再読み込みして確認してください。`);
  return row;
}

/** Missing counts are unknown, never zero. Shared by the dashboard and submission workflow. */
export function parsePayrollSourceState(value: unknown): PayrollSourceState {
  if (!object(value) || !text(value.fingerprint)
      || !['pendingAttendanceCount', 'pendingOvertimeCount', 'missingScheduleCount'].every(key => Number.isSafeInteger(value[key]) && value[key] >= 0)
      || (value.issues !== undefined && !Array.isArray(value.issues))) {
    throw new Error('勤怠確認状態の形式が不正です。未確認件数を取得し直してください。');
  }
  return value as PayrollSourceState;
}

function parseRevision(value: unknown, period: string, closingDay: number): SubmissionRevision {
  const bounds = payrollPeriod(period, closingDay);
  if (!object(value) || !text(value.id) || value.period_key !== period || value.closing_day !== closingDay
      || value.period_start !== bounds.start || value.period_end !== bounds.end
      || !Number.isSafeInteger(value.version) || value.version < 1 || !['confirmed', 'reopened'].includes(value.status)
      || !text(value.source_fingerprint) || !text(value.confirmed_by) || !text(value.confirmed_at) || !Number.isFinite(Date.parse(value.confirmed_at))
      || !object(value.rows) || value.rows.schemaVersion !== 1 || value.rows.timeEncoding !== 'decimal_hours'
      || !Array.isArray(value.rows.employees)
      || (value.status === 'confirmed' && (value.reopened_at !== null || value.reopen_reason !== null))
      || (value.status === 'reopened' && (!text(value.reopened_at) || !Number.isFinite(Date.parse(value.reopened_at)) || !text(value.reopen_reason)))) {
    throw new Error('保存済みの月次確定データの形式・対象月を確認できませんでした。');
  }
  if (validateLaborRows(value.rows.employees).length) throw new Error('保存済みの月次確定データに未確認・不正な提出値があります。');
  return value as SubmissionRevision;
}

export async function loadLatestSubmission(client: any, period: string, closingDay: number): Promise<SubmissionRevision | null> {
  payrollPeriod(period, closingDay);
  const { data, error } = await client.from('payroll_submission_revisions').select(REVISION_COLUMNS)
    .eq('period_key', period).eq('closing_day', closingDay).order('version', { ascending: false }).limit(1);
  if (error) throw new Error(`月次確定の読込に失敗しました。${error.message ?? ''}`);
  if (!Array.isArray(data) || data.length > 1) throw new Error('月次確定の読込結果を確認できませんでした。');
  return data.length ? parseRevision(data[0], period, closingDay) : null;
}
export async function getPayrollSourceState(client: any, period: string, closingDay: number): Promise<PayrollSourceState> {
  payrollPeriod(period, closingDay);
  return parsePayrollSourceState(requireResult(await client.rpc('get_payroll_source_state', { p_period_key: period, p_closing_day: closingDay }), '勤怠確認状態の読込'));
}
export function sourceBlockers(state: PayrollSourceState): string[] {
  parsePayrollSourceState(state);
  const messages: string[] = [];
  if (state.pendingAttendanceCount) messages.push(`未確定・打刻不足の勤怠 ${state.pendingAttendanceCount}件`);
  if (state.pendingOvertimeCount) messages.push(`未報告・未照合の時間外勤務 ${state.pendingOvertimeCount}件`);
  if (state.missingScheduleCount) messages.push(`勤務予定の設定不足 ${state.missingScheduleCount}件`);
  return messages;
}

/** Read all override pages under the same source fence as the displayed attendance. */
export async function loadPayrollOverrides(client: any, period: string, expectedFingerprint?: string): Promise<Map<string, Map<string, number>>> {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) throw new Error('給与調整値の対象月が不正です。');
  const verify = async () => {
    if (expectedFingerprint && (await getPayrollSourceState(client, period, 15)).fingerprint !== expectedFingerprint) {
      throw new Error('給与調整値の読み込み中に集計元が更新されました。勤怠から再読み込みしてください。');
    }
  };
  await verify();
  const overrides = new Map<string, Map<string, number>>();
  for (let offset = 0; offset < 10000; offset += 200) {
    const { data, error } = await client.from('payroll_overrides').select('employee_id,field_name,override_value')
      .eq('period_key', period).order('employee_id').order('field_name').range(offset, offset + 199);
    if (error) throw error;
    if (!Array.isArray(data)) throw new Error('給与調整値の応答を確認できません。');
    for (const row of data) {
      const numeric = typeof row?.override_value === 'number' || (typeof row?.override_value === 'string' && row.override_value.trim() !== '');
      const value = numeric ? Number(row.override_value) : NaN;
      if (!row || row.employee_id === null || row.employee_id === undefined || !String(row.employee_id).trim()
          || !text(row.field_name) || !Number.isFinite(value) || value < 0) throw new Error('給与調整値に未登録・不正な値があります。');
      const id = String(row.employee_id);
      if (!overrides.has(id)) overrides.set(id, new Map());
      if (overrides.get(id)!.has(row.field_name)) throw new Error('給与調整値の従業員・項目が重複しています。');
      overrides.get(id)!.set(row.field_name, value);
    }
    if (data.length < 200) { await verify(); return overrides; }
  }
  throw new Error('給与調整値が取得上限を超えました。');
}

export async function confirmSubmission(client: any, period: string, closingDay: number, snapshot: LaborSnapshot, fingerprint: string): Promise<SubmissionRevision> {
  payrollPeriod(period, closingDay);
  if (!snapshot || snapshot.schemaVersion !== 1 || snapshot.timeEncoding !== 'decimal_hours') throw new Error('提出データの形式が不正です。');
  const issues = validateLaborRows(snapshot.employees);
  if (issues.length) throw new Error(issues.slice(0, 5).join('\n'));
  if (!text(fingerprint)) throw new Error('集計データの確認が完了していません。再読み込みしてください。');
  const submitted: LaborSnapshot = JSON.parse(JSON.stringify(snapshot));
  const state = await getPayrollSourceState(client, period, closingDay);
  if (state.fingerprint !== fingerprint) throw new Error('確認中に勤怠や設定が更新されました。最新の集計を読み込み、再確認してください。');
  const blockers = sourceBlockers(state);
  if (blockers.length) throw new Error(`月次確定の前に確認が必要です: ${blockers.join('、')}`);
  const receipt = parseRevision(requireResult(await client.rpc('confirm_payroll_submission', {
    p_period_key: period, p_closing_day: closingDay, p_rows: submitted, p_source_fingerprint: fingerprint,
  }), '月次確定'), period, closingDay);
  if (receipt.status !== 'confirmed' || receipt.source_fingerprint !== fingerprint || canonical(receipt.rows) !== canonical(submitted)) {
    throw new Error('月次確定の保存応答が送信内容と一致しません。再読み込みして確認してください。');
  }
  return verifySavedRevision(client, receipt);
}
export async function reopenSubmission(client: any, revisionId: string, reason: string): Promise<SubmissionRevision> {
  if (!reason.trim() || reason.trim().length > 2000) throw new Error('確定を解除する理由を1〜2000文字で入力してください。');
  const row = requireResult(await client.rpc('reopen_payroll_submission', { p_revision_id: revisionId, p_reason: reason.trim() }), '月次確定の解除');
  const receipt = parseRevision(row, row.period_key, row.closing_day);
  if (receipt.id !== revisionId || receipt.status !== 'reopened' || receipt.reopen_reason !== reason.trim()) {
    throw new Error('確定解除の保存応答を確認できません。再読み込みして確認してください。');
  }
  return verifySavedRevision(client, receipt);
}

async function verifySavedRevision(client: any, expected: SubmissionRevision): Promise<SubmissionRevision> {
  let saved: SubmissionRevision | null;
  try { saved = await loadLatestSubmission(client, expected.period_key, expected.closing_day); }
  catch { throw new Error('保存処理後の再読込に失敗しました。保存済みの可能性があるため、再読み込みして確認してください。'); }
  if (!saved || canonical(saved) !== canonical(expected)) throw new Error('保存処理後の月次確定内容が一致しません。再読み込みして確認してください。');
  return saved;
}
